import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import usePersistedState from "@/lib/usePersistedState";
import { PageHeader } from "@/components/Layout";
import PeriodPicker, { presetRange } from "@/components/PeriodPicker";
import { inr } from "@/lib/utils2";
import { Card } from "@/components/ui/card";
import {
  ArrowUpRight, TrendingUp, Truck, FileText, Wallet,
  Package, ArrowDownToLine, ArrowUpFromLine,
} from "lucide-react";

// Vibrant KPI tile — gradient background, icon, large numeric value.
function KPITile({ label, value, sub, icon: Icon, gradient, accent, to, testId }) {
  const content = (
    <Card data-testid={testId}
      className={`group relative overflow-hidden p-5 rounded-sm border-0 shadow-sm hover:shadow-md transition-all duration-200 ${gradient}`}>
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-3">
          <div className={`text-[10px] uppercase tracking-[0.22em] font-semibold ${accent || "text-white/70"}`}>{label}</div>
          {Icon && <Icon className={`h-4 w-4 ${accent || "text-white/60"}`} />}
        </div>
        <div className={`font-serif text-3xl tabular-nums tracking-tight ${accent || "text-white"}`}>
          {value}
        </div>
        {sub && <div className={`text-xs mt-2 flex items-center gap-1 ${accent || "text-white/70"}`}>
          {sub}
          {to && <ArrowUpRight className="h-3 w-3 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />}
        </div>}
      </div>
      {/* decorative gradient orb */}
      <div className="absolute -right-8 -bottom-8 h-32 w-32 rounded-full bg-white/10 blur-2xl group-hover:scale-125 transition-transform duration-500"></div>
    </Card>
  );
  return to ? <Link to={to}>{content}</Link> : content;
}

// Simple horizontal bar — used to visualise aging buckets at a glance
function MiniBar({ segments }) {
  const total = segments.reduce((s, x) => s + x.value, 0);
  if (total <= 0) return <div className="h-1.5 w-full rounded-full bg-brand-line"></div>;
  return (
    <div className="flex h-1.5 w-full rounded-full overflow-hidden bg-brand-line">
      {segments.map((s, i) => (
        <div key={i} className={s.cls} style={{ width: `${(s.value / total) * 100}%` }} title={`${s.label}: ${inr(s.value)}`}></div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [parties, setParties] = useState([]);
  const [transporters, setTransporters] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [aging, setAging] = useState([]);
  const [truckStatus, setTruckStatus] = useState({ trucks: [], totals: {} });
  const [period, setPeriod] = usePersistedState("period:dash", () => presetRange("this-month"));

  const load = async () => {
    const params = { date_from: period.from, date_to: period.to };
    const [s, p, t, m, a, ts] = await Promise.all([
      api.get("/dashboard/stats", { params }),
      api.get("/dashboard/party-summary", { params }),
      api.get("/dashboard/transporter-summary", { params }),
      api.get("/dashboard/material-summary", { params }),
      api.get("/dashboard/aging"),
      api.get("/dashboard/truck-payment-status", { params }),
    ]);
    setStats(s.data); setParties(p.data); setTransporters(t.data);
    setMaterials(m.data); setAging(a.data); setTruckStatus(ts.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [period.from, period.to]);

  const totalAging = aging.reduce((s, a) => ({
    b0: s.b0 + (a.bucket_0_30 || 0),
    b1: s.b1 + (a.bucket_31_60 || 0),
    b2: s.b2 + (a.bucket_60_plus || 0),
  }), { b0: 0, b1: 0, b2: 0 });

  return (
    <div className="p-6 md:p-10 lg:p-12 max-w-[1500px] mx-auto" data-testid="dashboard-page">
      <PageHeader
        title="Dashboard"
        subtitle="Live snapshot of bills, ledgers, and operational metrics across parties, transporters and materials."
        action={
          <PeriodPicker value={period} onChange={(p) => setPeriod({ from: p.from, to: p.to })}
            defaultPreset="this-month" testIdPrefix="dash-period" />
        }
      />

      {/* KPI grid — vibrant gradients */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <KPITile testId="kpi-total-billed" label="Total Billed" value={inr(stats?.total_billed)}
          sub={`${stats?.bills_count || 0} bills issued`} icon={FileText} to="/bills"
          gradient="bg-gradient-to-br from-emerald-700 via-emerald-600 to-teal-600" />
        <KPITile testId="kpi-receivable" label="Receivable" value={inr(stats?.outstanding_receivable)}
          sub="from parties" icon={ArrowDownToLine} to="/payments-received"
          gradient="bg-gradient-to-br from-blue-700 via-indigo-600 to-blue-600" />
        <KPITile testId="kpi-payable" label="Payable" value={inr(stats?.outstanding_payable)}
          sub="to transporters" icon={ArrowUpFromLine} to="/payments-made"
          gradient="bg-gradient-to-br from-rose-700 via-red-600 to-orange-600" />
        <KPITile testId="kpi-profit" label="Net Profit" value={inr(stats?.total_profit)}
          sub="from truck releases" icon={TrendingUp}
          gradient="bg-gradient-to-br from-amber-600 via-orange-500 to-yellow-600" />
        <KPITile testId="kpi-unbilled" label="Unbilled Trucks" value={stats?.unbilled_trucks ?? "—"}
          sub={`${stats?.trucks_count || 0} total trucks`} icon={Truck} to="/trucks"
          gradient="bg-gradient-to-br from-violet-700 via-purple-600 to-fuchsia-600" />
      </div>

      {/* Aging — highlighted block with colored buckets */}
      <Card className="rounded-sm border-brand-line shadow-none mb-6 overflow-hidden" data-testid="aging-card">
        <div className="bg-gradient-to-r from-brand-forest via-emerald-800 to-teal-800 text-white px-5 py-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-serif text-xl">Receivable Aging</div>
              <div className="text-xs text-white/70 mt-0.5">Click any row to open that party's ledger</div>
            </div>
            <div className="flex gap-4 text-right text-xs">
              <div>
                <div className="text-emerald-200/80 uppercase tracking-wider text-[9px]">0–30 days</div>
                <div className="font-serif text-lg tabular-nums">{inr(totalAging.b0)}</div>
              </div>
              <div>
                <div className="text-amber-200/80 uppercase tracking-wider text-[9px]">31–60 days</div>
                <div className="font-serif text-lg tabular-nums text-amber-100">{inr(totalAging.b1)}</div>
              </div>
              <div>
                <div className="text-rose-200/80 uppercase tracking-wider text-[9px]">60+ days</div>
                <div className="font-serif text-lg tabular-nums text-rose-200">{inr(totalAging.b2)}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-[10px] uppercase tracking-[0.15em] text-brand-muted">
              <tr>
                <th className="text-left px-5 py-2.5">Party</th>
                <th className="text-left px-3 py-2.5">Spread</th>
                <th className="text-right px-3 py-2.5 text-emerald-700">0–30 d</th>
                <th className="text-right px-3 py-2.5 text-amber-700">31–60 d</th>
                <th className="text-right px-3 py-2.5 text-rose-700">60+ d</th>
                <th className="text-right px-5 py-2.5">Total Due</th>
              </tr>
            </thead>
            <tbody>
              {aging.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-brand-muted text-xs">No outstanding receivables.</td></tr>
              )}
              {aging.slice(0, 10).map((a) => (
                <tr key={a.party_id} className="border-t border-brand-line hover:bg-secondary/40 cursor-pointer"
                  onClick={() => nav(`/ledger/party/${a.party_id}`)}
                  data-testid={`aging-row-${a.party_id}`}>
                  <td className="px-5 py-3 font-medium">{a.name}</td>
                  <td className="px-3 py-3 w-[180px]">
                    <MiniBar segments={[
                      { value: a.bucket_0_30, cls: "bg-emerald-500", label: "0-30" },
                      { value: a.bucket_31_60, cls: "bg-amber-500", label: "31-60" },
                      { value: a.bucket_60_plus, cls: "bg-rose-500", label: "60+" },
                    ]} />
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums text-emerald-700 font-medium">{inr(a.bucket_0_30)}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-amber-700 font-medium">{inr(a.bucket_31_60)}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-rose-700 font-medium">{inr(a.bucket_60_plus)}</td>
                  <td className="px-5 py-3 text-right tabular-nums font-semibold">{inr(a.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Two-column ledger summaries */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="rounded-sm border-brand-line shadow-none overflow-hidden" data-testid="party-summary-card">
          <div className="bg-gradient-to-r from-blue-700 to-indigo-700 text-white px-5 py-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-serif text-xl flex items-center gap-2"><Wallet className="h-4 w-4" />Party-wise Outstanding</div>
                <div className="text-xs text-blue-100/80 mt-0.5">Debit minus credit · click row for ledger</div>
              </div>
              <Link to="/parties" className="text-xs text-blue-100 hover:text-white hover:underline">Manage →</Link>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.15em] text-brand-muted">
                <tr>
                  <th className="text-left px-5 py-2.5">Party</th>
                  <th className="text-right px-3 py-2.5">Debit</th>
                  <th className="text-right px-3 py-2.5">Credit</th>
                  <th className="text-right px-5 py-2.5">Balance</th>
                </tr>
              </thead>
              <tbody>
                {parties.length === 0 && (
                  <tr><td colSpan={4} className="px-5 py-6 text-center text-brand-muted text-xs">No parties yet</td></tr>
                )}
                {parties.slice(0, 8).map((p) => (
                  <tr key={p.party_id}
                    className="border-t border-brand-line hover:bg-blue-50/40 cursor-pointer"
                    onClick={() => nav(`/ledger/party/${p.party_id}`)}
                    data-testid={`party-row-${p.party_id}`}>
                    <td className="px-5 py-3 font-medium">{p.name}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{inr(p.debit)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-emerald-700">{inr(p.credit)}</td>
                    <td className={`px-5 py-3 text-right tabular-nums font-semibold ${p.balance > 0 ? "text-rose-600" : "text-brand-muted"}`}>
                      {inr(p.balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="rounded-sm border-brand-line shadow-none overflow-hidden" data-testid="transporter-summary-card">
          <div className="bg-gradient-to-r from-rose-700 to-orange-700 text-white px-5 py-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-serif text-xl flex items-center gap-2"><Truck className="h-4 w-4" />Transporter-wise Payable</div>
                <div className="text-xs text-rose-100/80 mt-0.5">Purchase minus paid</div>
              </div>
              <Link to="/transporters" className="text-xs text-rose-100 hover:text-white hover:underline">Manage →</Link>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.15em] text-brand-muted">
                <tr>
                  <th className="text-left px-5 py-2.5">Transporter</th>
                  <th className="text-right px-3 py-2.5">Purchase</th>
                  <th className="text-right px-3 py-2.5">Paid</th>
                  <th className="text-right px-5 py-2.5">Balance</th>
                </tr>
              </thead>
              <tbody>
                {transporters.length === 0 && (
                  <tr><td colSpan={4} className="px-5 py-6 text-center text-brand-muted text-xs">No transporters yet</td></tr>
                )}
                {transporters.slice(0, 8).map((t) => (
                  <tr key={t.transporter_id}
                    className="border-t border-brand-line hover:bg-rose-50/40 cursor-pointer"
                    onClick={() => nav(`/ledger/transporter/${t.transporter_id}`)}
                    data-testid={`transporter-row-${t.transporter_id}`}>
                    <td className="px-5 py-3 font-medium">{t.name}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{inr(t.credit)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-emerald-700">{inr(t.debit)}</td>
                    <td className={`px-5 py-3 text-right tabular-nums font-semibold ${t.balance > 0 ? "text-rose-600" : "text-brand-muted"}`}>
                      {inr(t.balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Truck-wise transporter payable — partial/paid/balance per truck */}
      <Card className="rounded-sm border-brand-line shadow-none mb-6 overflow-hidden" data-testid="truck-payable-card">
        <div className="bg-gradient-to-r from-orange-700 via-red-600 to-rose-700 text-white px-5 py-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-serif text-xl flex items-center gap-2"><Truck className="h-4 w-4" />Truck-wise Transporter Payable</div>
              <div className="text-xs text-orange-100/80 mt-0.5">For each truck — total owed (incl. inbound GST) vs paid vs balance. Sorted by largest outstanding.</div>
            </div>
            <div className="flex gap-4 text-right text-xs">
              <div>
                <div className="text-orange-200/80 uppercase tracking-wider text-[9px]">Trucks</div>
                <div className="font-serif text-lg tabular-nums">{truckStatus.totals?.trucks || 0}</div>
              </div>
              <div>
                <div className="text-orange-200/80 uppercase tracking-wider text-[9px]">Owed (incl GST)</div>
                <div className="font-serif text-lg tabular-nums">{inr(truckStatus.totals?.owed)}</div>
              </div>
              <div>
                <div className="text-emerald-200/80 uppercase tracking-wider text-[9px]">Paid</div>
                <div className="font-serif text-lg tabular-nums text-emerald-100">{inr(truckStatus.totals?.paid)}</div>
              </div>
              <div>
                <div className="text-rose-200/80 uppercase tracking-wider text-[9px]">Balance</div>
                <div className="font-serif text-lg tabular-nums text-rose-100">{inr(truckStatus.totals?.balance)}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.15em] text-brand-muted">
              <tr>
                <th className="text-left px-5 py-2.5">Date</th>
                <th className="text-left px-3 py-2.5">Vehicle</th>
                <th className="text-left px-3 py-2.5">Transporter</th>
                <th className="text-right px-3 py-2.5">Material ₹</th>
                <th className="text-right px-3 py-2.5">Transport ₹</th>
                <th className="text-right px-3 py-2.5">GST</th>
                <th className="text-right px-3 py-2.5">Total Owed</th>
                <th className="text-right px-3 py-2.5">Paid</th>
                <th className="text-right px-3 py-2.5">Balance</th>
                <th className="text-center px-3 py-2.5">Status</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {(truckStatus.trucks || []).length === 0 && (
                <tr><td colSpan={11} className="px-5 py-6 text-center text-brand-muted text-xs">No outstanding trucks in this period.</td></tr>
              )}
              {(truckStatus.trucks || []).slice(0, 12).map((r) => (
                <tr key={r.truck_id}
                  onClick={() => nav(`/ledger/transporter/${r.transporter_id}`)}
                  className="border-t border-brand-line hover:bg-rose-50/40 cursor-pointer"
                  data-testid={`truck-payable-row-${r.truck_id}`}>
                  <td className="px-5 py-2.5 whitespace-nowrap text-xs">{r.entry_date}</td>
                  <td className="px-3 py-2.5 font-medium">{r.vehicle_number}</td>
                  <td className="px-3 py-2.5">{r.transporter_name}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-brand-muted">{inr(r.material_cost)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-brand-muted">{inr(r.transport_cost)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    {r.purchase_gst_amount > 0 ? (
                      <span className="text-amber-700 font-medium" title={`${r.purchase_gst_percent}%`}>
                        {inr(r.purchase_gst_amount)} <span className="text-[9px] text-brand-muted">({r.purchase_gst_percent}%)</span>
                      </span>
                    ) : <span className="text-brand-muted">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{inr(r.total_owed)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-emerald-700">{inr(r.paid)}</td>
                  <td className={`px-3 py-2.5 text-right tabular-nums font-semibold ${
                    r.balance > 0 ? "text-rose-600" : "text-brand-muted"
                  }`}>{inr(r.balance)}</td>
                  <td className="px-3 py-2.5 text-center">
                    <span className={`px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider font-semibold ${
                      r.status === "SETTLED" ? "bg-emerald-100 text-emerald-700" :
                      r.status === "PARTIAL" ? "bg-amber-100 text-amber-700" :
                      "bg-rose-100 text-rose-700"
                    }`}>{r.status}</span>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <Link to={`/payments-made`} className="text-[11px] text-brand-muted hover:text-brand-ink hover:underline">Pay →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {(truckStatus.trucks || []).length > 12 && (
          <div className="px-5 py-3 text-[11px] text-brand-muted bg-secondary/30 border-t border-brand-line">
            Showing top 12 of {truckStatus.trucks.length} trucks · open a transporter's ledger for the full list.
          </div>
        )}
      </Card>

      <Card className="rounded-sm border-brand-line shadow-none mb-6 overflow-hidden" data-testid="material-summary-card">
        <div className="bg-gradient-to-r from-amber-700 to-yellow-700 text-white px-5 py-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-serif text-xl flex items-center gap-2"><Package className="h-4 w-4" />Material-wise Sales</div>
              <div className="text-xs text-amber-100/80 mt-0.5">Aggregated across material bills</div>
            </div>
            <Link to="/materials" className="text-xs text-amber-100 hover:text-white hover:underline">Manage materials →</Link>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.15em] text-brand-muted">
              <tr>
                <th className="text-left px-5 py-2.5">Material</th>
                <th className="text-left px-3 py-2.5">HSN</th>
                <th className="text-right px-3 py-2.5">Trips</th>
                <th className="text-right px-3 py-2.5">Weight</th>
                <th className="text-right px-3 py-2.5">Bill Amount</th>
                <th className="text-right px-5 py-2.5">Profit</th>
              </tr>
            </thead>
            <tbody>
              {materials.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-6 text-center text-brand-muted text-xs">No material sales yet</td></tr>
              )}
              {materials.map((m) => (
                <tr key={m.material_id}
                  className="border-t border-brand-line hover:bg-amber-50/40 cursor-pointer"
                  onClick={() => nav(`/ledger/material/${m.material_id}`)}
                  data-testid={`material-row-${m.material_id}`}>
                  <td className="px-5 py-3 font-medium">{m.name}</td>
                  <td className="px-3 py-3 text-brand-muted">{m.hsn || "—"}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{m.trips}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{m.weight}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{inr(m.bill_amount)}</td>
                  <td className="px-5 py-3 text-right tabular-nums text-emerald-700 font-semibold">{inr(m.profit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
